// Collection + search pages (sections/main-collection.liquid, sections/main-search.liquid).
// Everything here enhances plain links / GET forms that already work without JS:
//   - filter form (checkboxes, price) and filter/sort/chip links load in place through the
//     Section Rendering API (?section_id=…), with history entries and a polite status message;
//   - price range: the two sliders and the Van/Tot inputs stay in sync;
//   - < 1200px the filter form is a modal drawer (Figma Filter Drawer 276:6723): focus trap,
//     Escape / scrim / "Sluiten ×" / "Toon N producten" close it, focus returns to "Filter";
//   - "Laad meer producten" appends the next page's cards instead of navigating.
(function () {
  const DESKTOP = window.matchMedia("(min-width: 1200px)");
  let historyTouched = false;

  function init(root) {
    if (!root || root.dataset.plpReady) return;
    root.dataset.plpReady = "true";
    const sectionId = root.dataset.plpSection;
    let controller = null;

    const sectionUrl = (url) => {
      const target = new URL(url, window.location.origin);
      target.searchParams.set("section_id", sectionId);
      return target.toString();
    };

    async function fetchSection(url) {
      controller?.abort();
      controller = new AbortController();
      const response = await fetch(sectionUrl(url), { signal: controller.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return new DOMParser().parseFromString(await response.text(), "text/html");
    }

    // --- In-place loading (filters, sort, chips) --------------------------------------------
    function swap(selector, doc) {
      const current = root.querySelector(selector);
      const next = doc.querySelector(selector);
      if (current && next) current.innerHTML = next.innerHTML;
    }

    async function load(url, { push = true } = {}) {
      root.setAttribute("aria-busy", "true");
      try {
        const doc = await fetchSection(url);
        const focusedId = document.activeElement?.id;
        const scroller = root.querySelector("[data-plp-filters-scroll]");
        const scrollTop = scroller?.scrollTop || 0;

        swap("[data-plp-results]", doc);
        swap("[data-plp-filters-body]", doc);
        swap("[data-plp-show-count]", doc);
        if (scroller) scroller.scrollTop = scrollTop;
        if (focusedId && !document.activeElement?.closest("[data-plp-section]")) {
          document.getElementById(focusedId)?.focus({ preventScroll: true });
        }
        initPrice();
        updateFade();

        const count = doc.querySelector(".plp-toolbar__count")?.textContent.trim();
        const statusEl = root.querySelector("[data-plp-status]");
        if (statusEl && count) statusEl.textContent = count;

        const clean = new URL(url, window.location.origin);
        clean.searchParams.delete("section_id");
        if (push) {
          history.pushState({ plp: true }, "", clean.pathname + clean.search);
          historyTouched = true;
        }
      } catch (error) {
        if (error.name !== "AbortError") window.location.href = url; // fall back to a full load
      } finally {
        root.removeAttribute("aria-busy");
      }
    }

    // --- Filter form -----------------------------------------------------------------------
    const form = root.querySelector("[data-plp-filter-form]");

    function formUrl() {
      const params = new URLSearchParams();
      new FormData(form).forEach((value, key) => {
        if (value === "") return;
        params.append(key, value);
      });
      // Drop price bounds that equal the full range (the inputs always carry a value).
      root.querySelectorAll("[data-plp-price]").forEach((group) => {
        const max = Number(group.dataset.max);
        const minInput = group.querySelector('[data-plp-price-input="min"]');
        const maxInput = group.querySelector('[data-plp-price-input="max"]');
        if (minInput && Number(minInput.value) <= 0) params.delete(minInput.name);
        if (maxInput && Number(maxInput.value) >= max) params.delete(maxInput.name);
      });
      params.delete("page");
      const query = params.toString();
      return form.getAttribute("action") + (query ? `?${query}` : "");
    }

    if (form) {
      form.addEventListener("change", (event) => {
        if (event.target.matches("[data-plp-price-range]")) return; // handled on release below
        load(formUrl());
      });
      form.addEventListener("submit", (event) => {
        event.preventDefault();
        load(formUrl());
      });
    }

    root.addEventListener("click", (event) => {
      const link = event.target.closest("a[data-plp-link]");
      if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
      event.preventDefault();
      link.closest("details")?.removeAttribute("open");
      load(link.href);
    });

    // --- Price range -----------------------------------------------------------------------
    function initPrice() {
      root.querySelectorAll("[data-plp-price]").forEach((group) => {
        const max = Number(group.dataset.max) || 1;
        const ranges = {
          min: group.querySelector('[data-plp-price-range="min"]'),
          max: group.querySelector('[data-plp-price-range="max"]'),
        };
        const inputs = {
          min: group.querySelector('[data-plp-price-input="min"]'),
          max: group.querySelector('[data-plp-price-input="max"]'),
        };
        const fill = group.querySelector("[data-plp-price-fill]");
        if (!ranges.min || !ranges.max || !inputs.min || !inputs.max) return;

        const paint = () => {
          const low = Math.min(Number(ranges.min.value), Number(ranges.max.value));
          const high = Math.max(Number(ranges.min.value), Number(ranges.max.value));
          // Native range thumbs travel between 8px and (width − 8px).
          fill.style.left = `calc(8px + (100% - 16px) * ${low / max})`;
          fill.style.right = `calc(8px + (100% - 16px) * ${1 - high / max})`;
        };
        if (group.dataset.plpPriceReady) {
          paint();
          return;
        }
        group.dataset.plpPriceReady = "true";

        ["min", "max"].forEach((side) => {
          ranges[side].addEventListener("input", () => {
            // Thumbs can't cross.
            if (side === "min" && Number(ranges.min.value) > Number(ranges.max.value)) ranges.min.value = ranges.max.value;
            if (side === "max" && Number(ranges.max.value) < Number(ranges.min.value)) ranges.max.value = ranges.min.value;
            inputs[side].value = ranges[side].value;
            paint();
          });
          ranges[side].addEventListener("change", () => load(formUrl()));
          inputs[side].addEventListener("input", () => {
            const value = Math.max(0, Math.min(max, Number(inputs[side].value) || 0));
            ranges[side].value = value;
            paint();
          });
        });
        paint();
      });
    }
    initPrice();

    // --- Filter drawer (< 1200px) ----------------------------------------------------------
    const drawer = root.querySelector("[data-plp-drawer]");
    const panel = root.querySelector("[data-plp-drawer-panel]");
    const scroller = root.querySelector("[data-plp-filters-scroll]");

    function updateFade() {
      if (!scroller) return;
      const atEnd = scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 2;
      scroller.classList.toggle("is-at-end", atEnd);
    }
    scroller?.addEventListener("scroll", updateFade, { passive: true });

    function openDrawer() {
      if (!drawer || DESKTOP.matches) return;
      drawer.classList.add("is-open");
      panel.setAttribute("role", "dialog");
      panel.setAttribute("aria-modal", "true");
      panel.setAttribute("tabindex", "-1");
      document.documentElement.classList.add("is-scroll-locked");
      root.querySelectorAll("[data-plp-filter-open]").forEach((button) => button.setAttribute("aria-expanded", "true"));
      panel.focus();
      updateFade();
    }
    function closeDrawer(returnFocus = true) {
      if (!drawer?.classList.contains("is-open")) return;
      drawer.classList.remove("is-open");
      panel.removeAttribute("role");
      panel.removeAttribute("aria-modal");
      panel.removeAttribute("tabindex");
      document.documentElement.classList.remove("is-scroll-locked");
      root.querySelectorAll("[data-plp-filter-open]").forEach((button) => button.setAttribute("aria-expanded", "false"));
      if (returnFocus) root.querySelector("[data-plp-filter-open]")?.focus();
    }

    root.addEventListener("click", (event) => {
      if (event.target.closest("[data-plp-filter-open]")) openDrawer();
      else if (event.target.closest("[data-plp-filter-close]")) closeDrawer();
    });
    root.addEventListener("keydown", (event) => {
      if (event.key === "Escape") {
        const openSort = root.querySelector("[data-plp-sort][open]");
        if (openSort) {
          openSort.removeAttribute("open");
          openSort.querySelector("summary")?.focus();
          return;
        }
        closeDrawer();
      } else if (event.key === "Tab" && drawer?.classList.contains("is-open")) {
        window.SlowBurnGoods?.trapFocus?.(event, panel);
      }
    });
    DESKTOP.addEventListener("change", (event) => {
      if (event.matches) closeDrawer(false);
    });

    // Close the sort menu on an outside click.
    document.addEventListener("click", (event) => {
      root.querySelectorAll("[data-plp-sort][open]").forEach((details) => {
        if (!details.contains(event.target)) details.removeAttribute("open");
      });
    });

    // --- Load more -------------------------------------------------------------------------
    root.addEventListener("click", async (event) => {
      const button = event.target.closest("a[data-plp-load-more]");
      if (!button || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
      event.preventDefault();
      if (button.getAttribute("aria-busy") === "true") return;
      button.setAttribute("aria-busy", "true");
      try {
        const doc = await fetchSection(button.href);
        const grid = button.closest("[data-plp-section]").querySelector("[data-plp-grid]");
        const nextItems = doc.querySelectorAll("[data-plp-grid] > *");
        const firstNew = nextItems[0];
        grid.append(...nextItems);
        const pagination = root.querySelector("[data-plp-pagination]");
        const nextPagination = doc.querySelector("[data-plp-pagination]");
        if (pagination && nextPagination) {
          nextPagination.querySelector(".plp-pagination__previous")?.remove(); // page 1 is on screen
          pagination.replaceWith(nextPagination);
        }
        firstNew?.querySelector("a.product-card__link")?.focus({ preventScroll: false });
      } catch (error) {
        if (error.name !== "AbortError") window.location.href = button.href;
      } finally {
        button.removeAttribute("aria-busy");
      }
    });
  }

  // Back/forward through in-place filter states: reload the page for that URL.
  // Every entry of this page is marked, so a popstate onto one (also after a reload) re-renders.
  if (document.querySelector("[data-plp-section]")) history.replaceState({ ...(history.state || {}), plp: true }, "");
  window.addEventListener("popstate", (event) => {
    if (historyTouched || event.state?.plp) window.location.reload();
  });

  document.querySelectorAll("[data-plp-section]").forEach(init);
  document.addEventListener("shopify:section:load", (event) => {
    event.target.querySelectorAll("[data-plp-section]").forEach(init);
  });
})();
